<template>
    <div class="predictor page page--light">
        <div class="predictions-margin">
            <header :class="['row__header', { 'row__header--partner-app': isPartnerApp }]">
                <div class="header-left">
                    <back-button :isAbsolute="false"
                        :isBlack="true"
                    />
                    <h1 id="predictor-overview-title"
                        class="predictor__title"
                    >
                        <template v-if="!fetchingCurrentTeam">
                            {{ currentTeam.name }}
                        </template>
                    </h1>
                </div>

                <router-link
                    v-if="showLeaderboardLink"
                    :to="leaderboardLink"
                    class="leaderboard-link"
                >
                    {{ $t("predictors.viewLeaderboard") }}
                </router-link>
            </header>

            <div
                class="
          flex flex--justify-space-between flex--align-center
          mobile-padding
        "
            >
                <div v-if="fetchingSportEvents"
                    class="matches-title--skeleton"
                />
                <h2 v-else
                    class="matches-title"
                >
                    {{ $t("predictors.gameStatus") }}
                </h2>
            </div>

            <section class="flex predictions-section mobile-padding">
                <div v-if="fetchingSportEvents"
                    class="status-switch--skeleton"
                >
                    <div class="status-switch--skeleton__switch" />
                    <div class="status-switch--skeleton__switch" />
                </div>
                <status-switch v-else
                    @onChangeStatus="handleStatusChange"
                />
                <div class="pagination-wrapper pagination-wrapper--first">
                    <pagination
                        v-show="pagination.lastPage > 1"
                        ref="predictorOverviewTopPagination"
                        :page="pagination.currentPage"
                        :last-page="pagination.lastPage"
                        @currentPage="handlePaginationUpdate"
                    />
                </div>

                <ul v-if="fetchingSportEvents"
                    class="flex flex--wrap predictions"
                >
                    <li
                        v-for="index of Array.from({ length: 3 })"
                        :key="index"
                        class="prediction-width flex flex--equal-distribution"
                    >
                        <prediction-tile-skeleton />
                    </li>
                </ul>
                <template v-else>
                    <ul class="flex flex--wrap predictions">
                        <li
                            v-for="sportEvent in currentTeamSportEvents"
                            :key="sportEvent.id"
                            class="prediction-width flex flex--equal-distribution"
                        >
                            <prediction-tile
                                :partnerId="partnerId"
                                :currencyId="currencyId"
                                :sportEventData="sportEvent"
                                :teamId="teamId"
                            />
                        </li>
                    </ul>

                    <section v-if="noSportEvents"
                        class="mobile-padding"
                    >
                        <div class="no-predictions">
                            {{ $t("predictors.noPredictionsAvailable") }}
                        </div>
                    </section>
                </template>

                <div class="pagination-wrapper">
                    <pagination
                        v-show="showBottomPagination"
                        :page="pagination.currentPage"
                        :last-page="pagination.lastPage"
                        @currentPage="handlePaginationUpdate"
                    />
                </div>
            </section>
        </div>
    </div>
</template>

<script>
import {
    mapActions, mapGetters,
} from 'vuex';

import backButton from '@webapp/components/back-button/back-button.vue';
import predictionTile from '@webapp/modules/fan-engagement/components/predictor/prediction-tile/prediction-tile.vue';
import predictionTileSkeleton from '@webapp/modules/fan-engagement/components/predictor/prediction-tile/prediction-tile-skeleton.vue';
import statusSwitch from '@webapp/modules/fan-engagement/components/predictor/status-switch';
import pagination from '@webapp/components/pagination/pagination';

import socios from '@webapp/socios.json';

import sharedEpicTypes from '@shared/epics/types.js';

// Types
import {
    RewardsRemoteConfigKeys,
} from '@shared/modules/rewards/types/constants';

export default {
    name: 'predictor-overview-v2',
    components: {
        backButton,
        predictionTile,
        predictionTileSkeleton,
        pagination,
        statusSwitch,
    },
    data() {
        return {
            isLeaderboardsEnabled: false,
        };
    },
    computed: {
        ...mapGetters([
            'isPartnerApp',
            'getLatestValidationError',
        ]),

        ...mapGetters('partners', ['partnerById']),

        ...mapGetters('predictorSportEvents', [
            'getSportEvents',
            'getSportEventsPagination',
            'getFetchingSportEvents',
        ]),

        ...mapGetters('predictorTeams', ['getFetchingCurrentTeam', 'getCurrentTeam', 'getTeamPartnerIdById']),
        ...mapGetters('predictorLeaderboards', ['getPredictionsLeaderboard']),
        ...mapGetters('leaderboards', ['getLeaderboards']),
        ...mapGetters('wallet', ['wallet']),

        ...mapGetters('remoteConfig', ['remoteConfigByKey']),

        fetchingSportEvents() {
            return this.getFetchingSportEvents;
        },

        fetchingCurrentTeam() {
            return this.getFetchingCurrentTeam;
        },

        teamId() {
            return this.$route.params.teamId ? this.$route.params.teamId : '';
        },

        partnerId() {
            return this.teamId ? this.getTeamPartnerIdById(this.teamId) : '';
        },

        currentTeam() {
            return this.getCurrentTeam || {};
        },

        noSportEvents() {
            return this.getSportEvents?.length < 1;
        },

        currentTeamSportEvents() {
            return Object.freeze(this.getSportEvents);
        },

        pagination() {
            return this.getSportEventsPagination;
        },

        showBottomPagination() {
            return this.pagination.lastPage > 1 && this.pagination.currentPage < this.pagination.lastPage;
        },

        showLeaderboardLink() {
            return this.isLeaderboardsEnabled && this.predictionsLeaderboard && this.currentTeamSportEvents;
        },

        leaderboardLink() {
            return '/rewards/leaderboard/' + this.predictionsLeaderboard.id;
        },

        predictionsLeaderboard() {
            if (this.partnerId) {
                return this.getPredictionsLeaderboard(this.partnerId);
            }

            return this.getPredictionsLeaderboard(this.getTeamPartnerIdById(this.teamId));
        },

        partner() {
            return this.partnerById(this.partnerId);
        },

        currencyId() {
            return this.partner?.currencyId || '';
        },
    },
    watch: {
        getLeaderboards: 'handleLeaderboards',
        predictionsLeaderboard: 'checkLeaderboards',
        getLatestValidationError: 'handleErrors',
    },
    async mounted() {
        if (this.teamId === '') {
            this.$router.push('/404');
        }

        // Useful check to avoid flickering behavior while loading new team
        if (!Object.keys(this.currentTeam).length || this.teamId !== this.currentTeam.id) {
            this.resetCurrentTeam();

            await this.fetchTeamById(this.teamId);
        }

        if (!this.wallet || !this.wallet.id) {
            await this.getWallet({
                chilizCurrencyId: socios.chilizCurrencyId,
            });
        }

        this.initSportEvents();
        this.checkLeaderboards();
    },
    methods: {
        ...mapActions('predictorLeaderboards', ['setPredictionsLeaderboard']),
        ...mapActions('predictorSportEvents', ['handleFetchFilteredSportEvents']),
        ...mapActions('predictorTeams', ['fetchTeamById', 'resetCurrentTeam']),
        ...mapActions('leaderboards', ['fetchLeaderboards']),

        ...mapActions({
            getWallet: sharedEpicTypes.GET_WALLET,
        }),

        async initSportEvents() {
            this.handleFetchFilteredSportEvents({
                page: 1,
                teamId: this.teamId,
                partnerId: this.partnerId,
                filterStatus: 'currentGames',
            });

            if (!this.predictionsLeaderboard && this.partnerId) {
                await this.fetchLeaderboards({
                    partnerIds: [this.partnerId],
                    statuses: ['open'],
                    types: ['partner'],
                });
            }
        },

        handleStatusChange(status) {
            this.handleFetchFilteredSportEvents({
                filterStatus: status,
                teamId: this.teamId,
                partnerId: this.partnerId,
            });
        },

        handlePaginationUpdate(page, filterStatus) {
            if (page !== this.pagination.currentPage) {
                this.handleFetchFilteredSportEvents({
                    page,
                    teamId: this.teamId,
                    partnerId: this.partnerId,
                    filterStatus,
                });

                window.scrollTo(0, this.$refs.predictorOverviewTopPagination.offsetTop);
            }
        },

        handleLeaderboards(leaderboards) {
            if (leaderboards.length) {
                this.setPredictionsLeaderboard({
                    leaderboard: leaderboards[0],
                    teamId: this.teamId,
                    partnerId: this.partnerId,
                });
            }
        },

        checkLeaderboards() {
            this.isLeaderboardsEnabled = Boolean(this.remoteConfigByKey(RewardsRemoteConfigKeys.IsWebLeaderboardsEnabled)) && this.predictionsLeaderboard;
        },

        handleErrors() {
            if (!this.team || (this.team && this.teamId !== this.team.id)) {
                this.$router.push('/404');
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.page {
    background: linear-gradient(to bottom, $white-color, $athens-gray-color);
    background-attachment: fixed;
}

.loading-spinner {
    align-self: center;
    height: 36rem;

    @include min-width(md) {
        height: 30rem;
    }
}

.row__header {
    display: flex;
    justify-content: space-between;
    margin-left: 1rem;

    @include min-width(md) {
        margin-left: 0;
    }

    @include min-width(lg) {
        margin-left: -5.4rem;
    }

    .predictor__title {
        margin: 1.65rem 0;
        min-height: 2.7rem;
        line-height: 2.7rem;

        @include min-width(md) {
            min-height: 3.4rem;
            line-height: 3.4rem;
        }
    }
}

.header-left {
    display: flex;
    align-items: center;
    flex: 1;
}

.matches-title--skeleton {
    margin-bottom: 1.5rem;
    width: 13.6rem;
    height: 3rem;
    background: $skeleton-gradient;
    border-radius: 19px;
}

.matches-title {
    font-size: 1.8rem;
}

.mobile-padding {
    @include max-width(md) {
        padding-left: 1rem;
    }
}

.leaderboard-link {
    font-size: 1.5rem;
    color: $lima-color;
    text-decoration: none;
    margin-right: 1rem;
}

.status-switch--skeleton {
    display: flex;
    align-items: center;

    &__switch {
        width: 12rem;
        height: 3rem;
        background: $skeleton-gradient;
        border-radius: 4px;

        &:first-child {
            margin-right: 1rem;
        }
    }
}

.pagination-wrapper {
    display: flex;
    margin-right: 1rem;

    &--first {
        margin-top: 2rem;
    }

    @include max-width(md) {
        align-self: center;
    }
}

.prediction-width {
    width: 100%;

    @include min-width(lg) {
        width: 50%;
    }
}

.predictions-margin {
    margin: 0;

    @include min-width(lg) {
        margin: 0 8rem;
    }

    @include min-width(xl) {
        margin: 0 15rem;
    }

    @include min-width(xxl) {
        margin: 0 25rem;
    }
}

.predictions-section {
    display: flex;
    flex-direction: column;
    margin-bottom: 3.6rem;

    & > .predictions {
        padding: 0;
        margin: 3rem 0 0 0;
    }
}

.no-predictions {
    height: 30rem;
    background-color: $white-color;
    border-radius: 10px;
    padding: 5rem 2rem;
    text-align: center;
    font-size: 1.5rem;
    margin: 1rem;
}
</style>